import { useMemo } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

function diffParts(target) {
  const now = new Date();
  let years = target.getFullYear() - now.getFullYear();
  let months = target.getMonth() - now.getMonth();
  let days = target.getDate() - now.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(target.getFullYear(), target.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const totalDaysLeft = Math.ceil((target - now) / 86400000);
  return { years, months, days, totalDaysLeft };
}

function plural(t, base, n) {
  const key = n === 1 ? `${base}_one` : `${base}_other`;
  return t(key, { n });
}

/**
 * Live "Time left to file" indicator based on accident date + state statute.
 * NY personal-injury statute = 3 years. NJ = 2 years.
 */
export default function StatuteCountdown({ accidentDate, state }) {
  const t = useT();
  const data = useMemo(() => {
    if (!accidentDate || !state) return null;
    const dt = new Date(accidentDate);
    if (Number.isNaN(+dt)) return null;
    const yearsLimit = state === "NY" ? 3 : 2;
    const target = new Date(dt);
    target.setFullYear(target.getFullYear() + yearsLimit);
    return { target, ...diffParts(target) };
  }, [accidentDate, state]);

  if (!data) return null;
  const expired = data.totalDaysLeft <= 0;
  const urgent = !expired && data.totalDaysLeft <= 180;

  const tone = expired
    ? "border-[#F5B6B0] bg-[#FDE8E6] text-[#B91C1C]"
    : urgent
      ? "border-[#fcd9a3] bg-[#fff5e6] text-[#B45309]"
      : "border-[#bce5cc] bg-[#E6F6ED] text-[#166534]";

  return (
    <div
      className={`mt-3 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${tone}`}
      data-testid="statute-countdown"
    >
      {expired ? (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      ) : (
        <Clock className="mt-0.5 h-4 w-4 shrink-0" />
      )}
      <div className="flex-1">
        {expired ? (
          <div className="font-bold leading-snug">{t("statute.expired")}</div>
        ) : (
          <>
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-80">
              {t("statute.title")}
            </div>
            <div className="mt-2 font-display text-lg font-bold leading-snug">
              {data.years > 0 && (
                <span>{plural(t, "statute.years", data.years)} </span>
              )}
              {data.months > 0 && (
                <span>{plural(t, "statute.months", data.months)} </span>
              )}
              <span>{plural(t, "statute.days", data.days)}</span>
            </div>
            {urgent && (
              <div className="mt-2 text-xs font-semibold uppercase tracking-wider">
                {t("statute.urgent")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
